import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../common/responsive';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { openModal } from '../../redux/slices/modals';
import { usePrivy } from '@privy-io/react-auth';
import { hapticsImpactLight } from '../../common/haptic';
import ScanIcon from '../../assets/icons/scan.svg?react';
import SwapIcon from '../../assets/icons/swap.svg?react';
import SwapFilledIcon from '../../assets/icons/swap-filled.svg?react';
import HomeIcon from '../../assets/icons/home.svg?react';
import HomeFilledIcon from '../../assets/icons/home-filled.svg?react';
import GiftIcon from '../../assets/icons/gift.svg?react';
import GiftFilledIcon from '../../assets/icons/gift-filled.svg?react';
import UsersIcon from '../../assets/icons/users.svg?react';
import CardIcon from '../../assets/icons/card.svg?react';
import CardFilledIcon from '../../assets/icons/card-filled.svg?react';
import SearchIcon from '../../assets/icons/search.svg?react';
import SearchFilledIcon from '../../assets/icons/search-filled.svg?react';
import RewardsIcon from '../../assets/icons/rewards.svg?react';
import RewardsFilledIcon from '../../assets/icons/rewards-filled.svg?react';
import { useState, useEffect } from 'react';

import {
  CapacitorBarcodeScanner,
  CapacitorBarcodeScannerAndroidScanningLibrary,
  CapacitorBarcodeScannerScanOrientation,
} from '@capacitor/barcode-scanner';
import { Html5QrcodeSupportedFormats } from '../../../node_modules/html5-qrcode/es2015/core';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { selectTokenByNetworkAndSymbol } from '../../redux/slices/tokenSlice';
import { useFlag } from '@unleash/proxy-client-react';

const Container = styled.div`
  background-color: ${({ theme }) => theme.background.primary};
  position: fixed;
  bottom: 0px;
  left: 0px;
  right: 0px;
  width: 100vw;
  height: calc(56px + env(safe-area-inset-bottom));
  padding-left: 15px;
  padding-right: 15px;
  z-index: 3;
  transition: transform 0.3s ease;
  transform: ${({ $isKeyboardVisible }) =>
    $isKeyboardVisible ? 'translateY(100%)' : 'translateY(0)'};
`;

const InnerContainer = styled.div`
  height: 48px;
  display: flex;
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.background.secondary};
`;

const OptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  border-top: 3px solid transparent;
  color: ${({ theme }) => theme.text.secondary};
  ${({ selected, theme }) =>
    selected &&
    `
      border-top: 3px solid ${theme.primary.primary};
      color: ${theme.primary.primary};
    `}
`;

const OptionText = styled.p`
  font-size: 11px;
  color: ${({ theme }) => theme.text.regular};
  ${({ selected, theme }) => selected && `color: ${theme.primary.primary};`}
  ${({ middle, theme }) => middle && `color: ${theme.background.primary};`}
  font-weight: 600;
`;

const MobileNavigation = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const lpd = searchParams.get('lpd');
  const { user } = usePrivy();
  const enableCards = useFlag('enableCards');
  const enableSpend = useFlag('enableSpend');
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  // Hide navigation when keyboard is visible
  // Use Ionic's keyboard events (works on both native and web)
  useEffect(() => {
    if (!isMobile) return;

    const handleKeyboardShow = () => setIsKeyboardVisible(true);
    const handleKeyboardHide = () => setIsKeyboardVisible(false);

    // Primary: Ionic's keyboard events (works on native and mobile web)
    window.addEventListener('ionKeyboardDidShow', handleKeyboardShow);
    window.addEventListener('ionKeyboardDidHide', handleKeyboardHide);

    // Secondary: Capacitor Keyboard plugin for native (better timing)
    let showListener, hideListener;
    if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Keyboard')) {
      showListener = Keyboard.addListener('keyboardWillShow', handleKeyboardShow);
      hideListener = Keyboard.addListener('keyboardWillHide', handleKeyboardHide);
    }

    return () => {
      window.removeEventListener('ionKeyboardDidShow', handleKeyboardShow);
      window.removeEventListener('ionKeyboardDidHide', handleKeyboardHide);
      showListener?.remove();
      hideListener?.remove();
    };
  }, [isMobile]);

  const homeSelected =
    location.pathname === '/app/home' ||
    (Capacitor.isNativePlatform() && location.pathname === '/');
  const rewardsSelected = location.pathname === '/rewards';
  const tradeSelected = location.pathname === '/app/trade';
  const discoverSelected = location.pathname === '/app/discover';
  const usdcToken = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, 'solana', 'USDC'),
  );
  const cardsSelected = location.pathname === '/app/cards' || location.pathname.startsWith('/app/spend')
  const giftSelected = location.pathname === '/gifts/create' || location.pathname.startsWith('/app/gifts/');

  if (!isMobile || !user || location.pathname === '/update-required' || lpd === 'yield') {
    return;
  }

  return (
    <Container $isKeyboardVisible={isKeyboardVisible}>
      <InnerContainer>
        <OptionContainer
          onClick={() => {
            hapticsImpactLight();
            navigate('/app/home');
          }}
          selected={homeSelected}
        >
          {homeSelected ? (
            <HomeFilledIcon
              width={22}
              height={22}
              style={{ color: 'inherit' }}
            />
          ) : (
            <HomeIcon width={22} height={22} style={{ color: 'inherit' }} />
          )}
          <OptionText selected={homeSelected}>{t('nav.home')}</OptionText>
        </OptionContainer>
        {enableCards && enableSpend && (
          <OptionContainer
            onClick={() => {
              hapticsImpactLight();
              navigate('/app/spend');
            }}
            selected={cardsSelected}
          >
            {cardsSelected ? (
              <CardFilledIcon
                width={22}
                height={22}
                style={{ color: 'inherit' }}
              />
            ) : (
              <CardIcon width={22} height={22} style={{ color: 'inherit' }} />
            )}
            <OptionText selected={cardsSelected}>{t('nav.spend')}</OptionText>
          </OptionContainer>
        )}
        <OptionContainer
          onClick={() => {
            hapticsImpactLight();
            navigate('/app/gifts/create');
          }}
          selected={giftSelected}
        >
          {giftSelected ? (
            <GiftFilledIcon
              width={22}
              height={22}
              style={{ color: 'inherit' }}
            />
          ) : (
            <GiftIcon width={22} height={22} style={{ color: 'inherit' }} />
          )}
          <OptionText selected={giftSelected}>{t('nav.gift')}</OptionText>
        </OptionContainer>
        {/* {Capacitor.isNativePlatform() &&
          Capacitor.isPluginAvailable('CapacitorBarcodeScanner') && (
            <OptionContainer
              onClick={async () => {
                hapticsImpactLight();
                const { ScanResult, format } =
                  await CapacitorBarcodeScanner.scanBarcode({
                    hint: Html5QrcodeSupportedFormats.QR_CODE,
                    scanInstructions: 'Point at the QR',
                    scanOrientation:
                      CapacitorBarcodeScannerScanOrientation.PORTRAIT,
                    android: {
                      scanningLibrary:
                        CapacitorBarcodeScannerAndroidScanningLibrary.MLKIT,
                    },
                  });
                if (ScanResult && ScanResult.includes(`?gift=`)) {
                  const giftCode = ScanResult.split('?gift=')?.[1];
                  if (giftCode) {
                    navigate(`/?gift=${giftCode}`, { replace: true });
                  }
                }
                console.log('ScanResult', ScanResult);
              }}
              selected={false}
            >
              <ScanIcon width={22} height={22} style={{ color: 'inherit' }} />
              <OptionText selected={false}>{t('mobileNav.scan')}</OptionText>
            </OptionContainer>
          )} */}
        {/* <OptionContainer
          onClick={() => {
            hapticsImpactLight();
            navigate('/app/trade');
          }}
          selected={tradeSelected}
        >
          {tradeSelected ? (
            <SwapFilledIcon
              width={22}
              height={22}
              style={{ color: 'inherit' }}
            />
          ) : (
            <SwapIcon width={22} height={22} style={{ color: 'inherit' }} />
          )}
          <OptionText selected={tradeSelected}>{t('mobileNav.trade')}</OptionText>
        </OptionContainer> */}
        <OptionContainer
          onClick={() => {
            hapticsImpactLight();
            navigate('/app/discover');
          }}
          selected={discoverSelected}
        >
          {discoverSelected ? (
            <SearchFilledIcon
              width={22}
              height={22}
              style={{ color: 'inherit' }}
            />
          ) : (
            <SearchIcon width={22} height={22} style={{ color: 'inherit' }} />
          )}
          <OptionText selected={discoverSelected}>{t('nav.discover')}</OptionText>
        </OptionContainer>
        <OptionContainer
          onClick={() => {
            hapticsImpactLight();
            navigate('/rewards');
          }}
          selected={rewardsSelected}
        >
          {rewardsSelected ? (
            <RewardsFilledIcon
              width={22}
              height={22}
              style={{ color: 'inherit' }}
            />
          ) : (
            <RewardsIcon width={22} height={22} style={{ color: 'inherit' }} />
          )}
          <OptionText selected={rewardsSelected}>{t('nav.rewards')}</OptionText>
        </OptionContainer>
      </InnerContainer>
    </Container>
  );
};

export default MobileNavigation;
